import type {
  CommonPeopleExperiencesNovaEvent,
  CopilotIdentifiers,
  EmailIdentifiers,
  FeedbackIdentifiers,
  MeetingIdentifiers,
  openLinkedInIdentifiers,
  PersonaIdentifiers,
  StorylinePostIdentifiers,
  TeamsConversationIdentifiers,
  UrlIdentifiers,
} from "./types";

import type {
  ExternalLoggerTelemetryEvent,
  PXNovaTelemetryEvent,
} from "@1js/midgard-types";
import { NOVA_TELEMETRY_EVENT_TYPE } from "@1js/midgard-types";

export type {
  CommonPeopleExperiencesNovaEvent,
  CopilotIdentifiers,
  EmailIdentifiers,
  FeedbackIdentifiers,
  MeetingIdentifiers,
  NovaEventCreator,
  PeopleExperiencesNovaEvent,
  PersonaIdentifiers,
  StorylinePostIdentifiers,
  TeamsConversationIdentifiers,
  UrlIdentifiers,
} from "./types";

export type StartChatEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<Originator, "startChat", PersonaIdentifiers>;

export type StartAudioCallEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "startAudioCall",
    PersonaIdentifiers
  >;

export type StartVideoCallEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "startVideoCall",
    PersonaIdentifiers
  >;

export type ComposeEmailEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "composeEmail",
    PersonaIdentifiers
  >;

export type OpenMeetingEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "openMeeting",
    MeetingIdentifiers
  >;

export type OpenUrlEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<Originator, "openUrl", UrlIdentifiers>;

export type ShowOrgChartEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "showOrgChart",
    PersonaIdentifiers
  >;

export type ShowLinkedInCallEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "showLinkedIn",
    PersonaIdentifiers
  >;

export type ShowSmartNotesCallEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "showSmartNotes",
    PersonaIdentifiers
  >;

export type OpenTeamsConversationEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "openTeamsConversation",
    TeamsConversationIdentifiers
  >;

export type OpenEmailEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<Originator, "openEmail", EmailIdentifiers>;

export type UploadPhotoEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "uploadPhoto",
    PersonaIdentifiers
  >;

export type OpenUserFeedbackEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "openUserFeedback",
    FeedbackIdentifiers
  >;

export type SendCopilotPromptEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "sendCopilotPrompt",
    CopilotIdentifiers
  >;

export type OpenLinkedInEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "openLinkedIn",
    openLinkedInIdentifiers
  >;

export type OpenStorylinePostEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "openStorylinePost",
    StorylinePostIdentifiers
  >;

export type ShowProfileCardEvent<Originator extends string> =
  CommonPeopleExperiencesNovaEvent<
    Originator,
    "showProfileCard",
    PersonaIdentifiers
  >;

export const pxNovaTelemetryEvent = (
  originator: string,
  telemetryEvent: ExternalLoggerTelemetryEvent
): PXNovaTelemetryEvent => ({
  originator,
  type: NOVA_TELEMETRY_EVENT_TYPE,
  data: () => telemetryEvent,
});
